import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import useAuthStore from "@/store/authStore";
import {
  allowedHTMLElements,
  createScopedLogger,
  rehypePlugins,
  remarkPlugins,
} from "@/utils";
import "highlight.js/styles/github-dark.css";
import "highlight.js/styles/github.css";
import { Globe } from "lucide-react";
import PropTypes from "prop-types";
import {
  lazy,
  memo,
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import styles from "./Markdown.module.scss";
import ShikiCodeBlock from "./ShikiCodeBlock";

// Component that breaks out of paragraph by rendering outside it
// This prevents <div> from being nested inside <p> tags
const BlockCodeWrapper = ({ children, node }) => {
  const [portalTarget, setPortalTarget] = useState(null);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);

  // Check if we're inside a paragraph from the node structure
  const isInsideParagraph =
    node?.parent?.type === "element" && node.parent.tagName === "p";

  // Use ref callback to check parent immediately when element mounts
  const setWrapperRef = (element) => {
    wrapperRef.current = element;
    if (element && isInsideParagraph) {
      const parent = element.parentElement;
      if (
        parent &&
        parent.tagName === "P" &&
        parent.parentNode &&
        !containerRef.current
      ) {
        // Create container immediately after the paragraph
        const container = document.createElement("div");
        parent.parentNode.insertBefore(container, parent.nextSibling);
        containerRef.current = container;
        setPortalTarget(container);
      }
    }
  };

  useLayoutEffect(() => {
    if (isInsideParagraph && wrapperRef.current) {
      const element = wrapperRef.current;
      const parent = element.parentElement;

      if (
        parent &&
        parent.tagName === "P" &&
        parent.parentNode &&
        !containerRef.current
      ) {
        // Create container after the paragraph
        const container = document.createElement("div");
        parent.parentNode.insertBefore(container, parent.nextSibling);
        containerRef.current = container;
        setPortalTarget(container);
      }
    }

    // Cleanup
    return () => {
      if (containerRef.current && containerRef.current.parentNode) {
        containerRef.current.remove();
        containerRef.current = null;
      }
    };
  }, [isInsideParagraph]);

  // If we're inside a paragraph and have portal target, render outside
  if (isInsideParagraph && portalTarget) {
    return (
      <>
        {/* Placeholder to maintain React structure */}
        <span ref={setWrapperRef} style={{ display: "none" }} />
        {/* Portal the actual content outside the paragraph */}
        {createPortal(children, portalTarget)}
      </>
    );
  }

  // Otherwise render normally
  return <div ref={setWrapperRef}>{children}</div>;
};

const Mermaid = lazy(() => import("./Mermaid"));

// Dynamic KaTeX CSS loading
let katexCSSLoaded = false;
const loadKatexCSS = async () => {
  if (!katexCSSLoaded) {
    try {
      await import("katex/dist/katex.min.css");
      katexCSSLoaded = true;
    } catch (error) {
      console.warn("Failed to load KaTeX CSS:", error);
    }
  }
};

// Add text size class mapping
const TEXT_SIZE_CLASSES = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl",
};

// Default text size
const DEFAULT_TEXT_SIZE = "md";

const logger = createScopedLogger("MarkdownComponent");
const MERMAID_REGEX =
  /^(graph|sequenceDiagram|classDiagram|erDiagram|gantt|pie|flowchart|stateDiagram|gitGraph|journey)(?:\s|$)/;

// Add citation regex pattern
const CITATION_REGEX = /\[\^(\d+?)?\^]|\[\[(\d+)]]|\[(\d+)](?!\()/g;

// Helper function to detect if a link is a citation link
const isCitationLink = (linkText, href, sources, parsedSources) => {
  if (!linkText || !href) {
    return false;
  }

  const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const isDomainLike = domainPattern.test(linkText.trim());
  const isShortDomain =
    linkText.includes(".") && !linkText.includes(" ") && linkText.length < 50;
  const isExternalUrl =
    href.startsWith("http") && !href.includes(window.location.hostname);
  const isShortDescriptive = linkText.length < 50 && !linkText.includes("http");

  const isCitation =
    isDomainLike || isShortDomain || (isExternalUrl && isShortDescriptive);

  return isCitation;
};

// Helper function to extract domain from URL
const extractDomain = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return url;
  }
};

// Helper function to get tooltip content for citation links
const getCitationTooltipContent = (linkText, href, parsedSources = []) => {
  const matchingSource = parsedSources.find((source) => {
    const urlMatch = source.url === href;
    const sourceDomain = extractDomain(source.url);
    const hrefDomain = extractDomain(href);
    const domainMatch = sourceDomain === hrefDomain;
    const titleMatch =
      source.title?.toLowerCase().includes(linkText.toLowerCase()) ||
      linkText.toLowerCase().includes(source.title?.toLowerCase() || "");

    return urlMatch || domainMatch || titleMatch;
  });

  if (matchingSource) {
    return {
      title: matchingSource.title || linkText,
      url: matchingSource.url,
      domain: extractDomain(matchingSource.url),
      description: matchingSource.description,
      favicon:
        matchingSource.favicon ||
        `https://www.google.com/s2/favicons?domain=${extractDomain(matchingSource.url)}&sz=32`,
    };
  }

  const domain = extractDomain(href);
  const title = linkText === domain ? domain : `${linkText} (${domain})`;

  return {
    title,
    url: href,
    domain,
    description: null,
    favicon: `https://www.google.com/s2/favicons?domain=${domain}&sz=32`,
  };
};

// Create a Citation component to enhance citation styling
const Citation = ({ index, onClick, href, title }) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (href) {
      // If we have a URL, open it in a new tab
      window.open(href, "_blank", "noopener,noreferrer");
    }

    // Also call the onClick handler if provided (for sidebar, etc.)
    if (onClick) {
      onClick(e);
    }
  };

  // If we have an href, make it a proper link
  if (href) {
    return (
      <a
        href={href}
        className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-md hover:bg-purple-200 dark:hover:bg-purple-800/50 transition-colors cursor-pointer group mx-0.5"
        onClick={handleClick}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={0}
        aria-label={title ? `Citation ${index}: ${title}` : `Citation ${index}`}
        title={title || `View source ${index}`}
      >
        <span className="font-mono">[{index}]</span>
        <span className="ml-1 max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-in-out opacity-0 group-hover:opacity-100">
          <span className="pl-1 text-xs">View source</span>
        </span>
      </a>
    );
  }

  // Fallback: no href, just a button
  return (
    <button
      className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-md hover:bg-purple-200 dark:hover:bg-purple-800/50 transition-colors cursor-pointer group mx-0.5"
      onClick={handleClick}
      tabIndex={0}
      aria-label={title ? `Citation ${index}: ${title}` : `Citation ${index}`}
      title={title || `View source ${index}`}
    >
      <span className="font-mono">[{index}]</span>
      <span className="ml-1 max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-in-out opacity-0 group-hover:opacity-100">
        <span className="pl-1 text-xs">View source</span>
      </span>
    </button>
  );
};

Citation.propTypes = {
  index: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onClick: PropTypes.func,
  href: PropTypes.string,
  title: PropTypes.string,
};

// Create a CitationLink component with tooltip for in-text citations
const CitationLink = ({
  href,
  children,
  linkText,
  parsedSources = [],
  ...props
}) => {
  const tooltipContent = getCitationTooltipContent(
    linkText,
    href,
    parsedSources
  );

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block cursor-pointer"
        >
          <Badge
            variant="outline"
            className="inline-flex items-center gap-1 leading-4 hover:bg-accent hover:text-accent-foreground transition-colors"
            {...props}
          >
            <span>{children}</span>
            <svg
              aria-hidden="true"
              height="7"
              viewBox="0 0 6 6"
              width="7"
              className="opacity-70"
            >
              <path
                d="M1.25215 5.54731L0.622742 4.9179L3.78169 1.75597H1.3834L1.38936 0.890915H5.27615V4.78069H4.40513L4.41109 2.38538L1.25215 5.54731Z"
                fill="currentColor"
              />
            </svg>
          </Badge>
        </a>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-xs"
        style={{
          zIndex: 99999,
        }}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-6 h-6 rounded-lg bg-background border-0 flex items-center justify-center overflow-hidden mt-0.5">
            {tooltipContent.favicon ? (
              <img
                src={tooltipContent.favicon}
                alt=""
                className="w-4 h-4"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : (
              <Globe className="w-4 h-4 text-muted-foreground" />
            )}
            <Globe
              className="w-4 h-4 text-muted-foreground"
              style={{ display: "none" }}
            />
          </div>
          <div className="flex-1 min-w-0 space-y-1">
            <div className="font-medium text-sm">{tooltipContent.title}</div>
            {tooltipContent.description && (
              <div className="text-xs text-muted-foreground">
                {tooltipContent.description}
              </div>
            )}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

CitationLink.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  linkText: PropTypes.string.isRequired,
  parsedSources: PropTypes.array,
};

// Add Mermaid detection function
const hasMermaidContent = (content) => {
  if (!content || typeof content !== "string") return false;
  return MERMAID_REGEX.test(content);
};

// Helper function to detect financial data patterns
// Used to override code block rendering when content is clearly financial data
const looksLikeFinancialData = (text) => {
  const trimmed = text.trim();

  // CRITICAL: Dollar signs followed by numbers should NEVER be treated as code
  if (/\$\s*\d/.test(trimmed)) {
    return true;
  }

  // Check for financial data patterns
  return (
    // Currency amounts
    /\$\d+[.,]?\d*\s*(USD|EUR|GBP|JPY|CNY|million|billion|thousand|M|B|K)?/i.test(
      trimmed
    ) ||
    // Numbers with billion/million/thousand
    /\d+[.,]?\d*\s*(billion|million|thousand|B|M|K)\b/i.test(trimmed) ||
    // Financial phrases with currency and units
    /\$\d+[.,]?\d*\s*(?:million|billion|thousand|M|B|K)\s+(?:in|of|for|from|to|representing|increase|decrease|growth|revenue|income|sales|earnings|profit|loss|cash|assets|liabilities|equity|capital|expenditures|capex|guidance|results|performance|quarter|Q\d|fiscal|year)/i.test(
      trimmed
    ) ||
    /\d+[.,]?\d*\s*(?:million|billion|thousand|M|B|K)\s+(?:in|of|for|from|to|representing|increase|decrease|growth|revenue|income|sales|earnings|profit|loss|cash|assets|liabilities|equity|capital|expenditures|capex|guidance|results|performance|quarter|Q\d|fiscal|year)/i.test(
      trimmed
    ) ||
    // Financial number + financial term
    (/\$\d+[.,]?\d*\s*(?:million|billion|thousand|M|B|K)/i.test(trimmed) &&
      /\b(revenue|income|sales|earnings|profit|loss|cash|assets|liabilities|equity|capital|expenditures|capex|guidance|results|performance|quarter|Q\d|fiscal|year|reported|representing|increase|decrease|growth)\b/i.test(
        trimmed
      )) ||
    (/\d+[.,]?\d*\s*(?:million|billion|thousand|M|B|K)\b/i.test(trimmed) &&
      /\b(revenue|income|sales|earnings|profit|loss|cash|assets|liabilities|equity|capital|expenditures|capex|guidance|results|performance|quarter|Q\d|fiscal|year|reported|representing|increase|decrease|growth)\b/i.test(
        trimmed
      )) ||
    // Ranges
    /\d+[.,]?\d*\s*-\s*\d+[.,]?\d*\s*(billion|million|thousand|B|M|K|range)?/i.test(
      trimmed
    ) ||
    /\$\d+[.,]?\d*\s*-\s*\$\d+[.,]?\d*/i.test(trimmed) ||
    // Percentages
    /[+-]?\d+[.,]?\d*%/i.test(trimmed) ||
    /\(\s*[+-]?\d+[.,]?\d*%\s*[A-Za-z]+\s*\)/i.test(trimmed) ||
    // Numbers with B/M/K suffixes
    /\d+[.,]?\d*[BMK]\b/i.test(trimmed) ||
    // Numbers in parentheses
    /\(\d+[.,]?\d*[BMK]?\)/i.test(trimmed) ||
    // Financial terms with numbers
    (/\b(Stock|Price|Performance|Capitalization|Range|Year-to-Date|52-Week|Current|Market|Down|Up|from|to|approximately|about|revenue|income|operating|financial|FY\d{4}|fiscal|earnings|profit|loss|margin|growth|metrics|Capex|Capital|Expenditures|Cash|Position|Free|Flow|Guidance|Q\d|quarter|trailing|twelve|months|reported|representing|increase|decrease|results|Results)\b/i.test(
      trimmed
    ) &&
      /\d+[.,]?\d*/.test(trimmed)) ||
    // Number ranges
    /\d+[.,]?\d*\s+to\s+\d+[.,]?\d*/.test(trimmed) ||
    // Financial metric labels
    /(?:Current|Year-to-Date|Market|52-Week|Range|Revenue|Income|Operating|Capital|Expenditures|Cash|Position|Free|Flow|Guidance|Q\d):/i.test(
      trimmed
    ) ||
    // Financial performance phrases
    /(?:record|marked|surpassed|reached|achieved|reported|representing)\s+(?:revenue|income|growth|performance|increase|decrease|results|Results)/i.test(
      trimmed
    ) ||
    // Multiple values
    /\d+[.,]?\d*\s*(?:billion|million|thousand|B|M|K)\s*[;,]?\s*\d+[.,]?\d*\s*(?:billion|million|thousand|B|M|K)/i.test(
      trimmed
    )
  );
};

// Add a memoized Mermaid component loader
const MemoizedMermaidComponent = memo(({ diagram, darkMode }) => {
  return (
    <Suspense
      fallback={
        <div className="relative overflow-hidden rounded-lg border border-border/50 bg-muted/30 p-4">
          <div className="flex items-center justify-center min-h-[100px]">
            <div className="flex items-center space-x-2 text-muted-foreground">
              <div className="h-4 w-32 animate-pulse rounded bg-muted" />
            </div>
          </div>
        </div>
      }
    >
      <Mermaid diagram={diagram} darkMode={darkMode} />
    </Suspense>
  );
});

MemoizedMermaidComponent.displayName = "MemoizedMermaidComponent";
MemoizedMermaidComponent.propTypes = {
  diagram: PropTypes.string.isRequired,
  darkMode: PropTypes.bool,
};

/**
 * Enhanced Markdown component with comprehensive syntax support
 */
export const Markdown = memo(
  ({
    children,
    html = false,
    limitedMarkdown = false,
    darkMode = false,
    onCitationClick,
    isStreaming = false,
    className,
    sources = null,
    isUser = false,
    ...props
  }) => {
    logger.trace("Render");

    // Get the user's text size preference from auth store
    const profile = useAuthStore((state) => state.profile);
    const textSize = profile?.settings?.system?.textSize || DEFAULT_TEXT_SIZE;
    const textSizeClass =
      TEXT_SIZE_CLASSES[textSize] || TEXT_SIZE_CLASSES[DEFAULT_TEXT_SIZE];

    // Load KaTeX CSS if math content is detected
    useEffect(() => {
      if (children && typeof children === "string") {
        const hasMath =
          /\$\$[\s\S]*?\$\$|\$[^$\n]+\$|\\begin\{[^}]+\}|\\end\{[^}]+\}/.test(
            children
          );
        if (hasMath) {
          loadKatexCSS();
        }
      }
    }, [children]);

    // Parse sources data
    const parsedSources = useMemo(() => {
      if (!sources) {
        return [];
      }

      let sourceData = sources;

      // Handle different source structures
      if (sources.result?.data) {
        sourceData = sources.result.data;
      } else if (sources.data && Array.isArray(sources.data)) {
        sourceData = sources.data;
      } else if (sources.sources && Array.isArray(sources.sources)) {
        sourceData = sources.sources;
      } else if (sources.result?.result?.data) {
        sourceData = sources.result.result.data;
      }

      if (Array.isArray(sourceData)) {
        sourceData = sourceData.filter(
          (item) => item && item.url && item.url !== null
        );
      }

      if (typeof sourceData === "string") {
        const results = [];
        const entries = sourceData
          .split("\n\n\n")
          .filter((entry) => entry.trim());

        for (const entry of entries) {
          const lines = entry.split("\n").filter((line) => line.trim());

          let url = "";
          let title = "";
          let description = "";

          for (const line of lines) {
            if (line.startsWith("URL: ")) {
              url = line.replace("URL: ", "").trim();
            } else if (line.startsWith("Title: ")) {
              title = line.replace("Title: ", "").trim();
            } else if (line.startsWith("Description: ")) {
              description = line.replace("Description: ", "").trim();
            }
          }

          if (url && title) {
            results.push({
              url,
              title,
              description,
              favicon: `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=32`,
            });
          }
        }

        return results;
      }

      if (Array.isArray(sourceData)) {
        const parsed = sourceData.map((source) => {
          let favicon = source.favicon;
          if (!favicon && source.url) {
            try {
              favicon = `https://www.google.com/s2/favicons?domain=${new URL(source.url).hostname}&sz=32`;
            } catch (error) {
              favicon = null;
            }
          }
          return {
            ...source,
            favicon,
          };
        });
        return parsed;
      }

      return [];
    }, [sources]);

    // Preprocess content to convert citation numbers to markdown links
    const processedContent = useMemo(() => {
      if (
        !children ||
        typeof children !== "string" ||
        !parsedSources ||
        parsedSources.length === 0
      ) {
        return children;
      }

      // Replace citation patterns like [1], [2], etc. with markdown links
      // Match [number] but not if it's already a markdown link [text](url) or inside code blocks
      let processed = children;

      // Simple regex to match [number] patterns (not already links)
      // This matches [1], [2], etc. but not [text](url) or inside code blocks
      const citationPattern = /\[(\d+)\](?!\()/g;

      processed = processed.replace(citationPattern, (match, citationNum) => {
        const citationNumber = parseInt(citationNum, 10);
        const source = parsedSources[citationNumber - 1]; // Citations are 1-indexed

        if (source && source.url) {
          // Convert to markdown link: [1](url)
          return `[${citationNum}](${source.url})`;
        }

        // If no source found, return original
        return match;
      });

      return processed;
    }, [children, parsedSources]);

    // SIMPLIFIED COMPONENTS - let SCSS handle styling
    const components = useMemo(() => {
      return {
        // Simplified link handler - no inline styles
        a: ({ node, href, children, ...props }) => {
          if (!href) return <span>{children}</span>;

          const linkText =
            typeof children === "string"
              ? children
              : Array.isArray(children)
                ? children.join("")
                : "";

          // Check if this is a citation link (matches pattern like [1], [2], etc.)
          const isCitationNumber = /^\[\d+\]$/.test(linkText.trim());

          // If it's a citation number link, find the source and render as citation
          if (isCitationNumber && parsedSources && parsedSources.length > 0) {
            const citationMatch = linkText.match(/\[(\d+)\]/);
            if (citationMatch) {
              const citationNumber = parseInt(citationMatch[1], 10);
              const source = parsedSources[citationNumber - 1]; // Citations are 1-indexed

              if (source && source.url === href) {
                // This is a citation link we created - render as Citation component
                return (
                  <Citation
                    index={citationNumber}
                    href={href}
                    title={source.title || null}
                    onClick={() =>
                      onCitationClick && onCitationClick(citationNumber)
                    }
                  />
                );
              }
            }
          }

          // Skip citation link processing if sources is null (e.g., in Sources section)
          // This ensures all links in the Sources section are rendered as plain <a> tags
          const isCitation =
            sources !== null &&
            isCitationLink(linkText, href, sources, parsedSources);

          const isExternalLink =
            !href.startsWith("sandbox:/") &&
            !href.startsWith("#") &&
            !href.startsWith("/");

          if (isCitation && isExternalLink && sources !== null) {
            return (
              <CitationLink
                href={href}
                linkText={linkText}
                parsedSources={parsedSources}
                {...props}
              >
                {children}
              </CitationLink>
            );
          }

          const target = isExternalLink ? "_blank" : undefined;
          const rel = isExternalLink ? "noopener noreferrer" : undefined;

          return (
            <a href={href} target={target} rel={rel} {...props}>
              {children}
              {isExternalLink && (
                <svg
                  aria-hidden="true"
                  height="7"
                  viewBox="0 0 6 6"
                  width="7"
                  className="opacity-70"
                  style={{
                    display: "inline",
                    verticalAlign: "baseline",
                    marginLeft: "2px",
                  }}
                >
                  <path
                    d="M1.25215 5.54731L0.622742 4.9179L3.78169 1.75597H1.3834L1.38936 0.890915H5.27615V4.78069H4.40513L4.41109 2.38538L1.25215 5.54731Z"
                    fill="currentColor"
                  />
                </svg>
              )}
            </a>
          );
        },

        img({ node, ...props }) {
          return <img className="max-w-[67%]" {...props} />;
        },

        // Simplified pre/code handler - no inline styles
        pre: (props) => {
          const { children, node } = props;
          const [firstChild] = node?.children ?? [];

          if (firstChild?.type === "element" && firstChild.tagName === "code") {
            const { className: codeClassName } = firstChild.properties || {};
            const content = firstChild.children?.[0]?.value ?? "";

            const isMermaid =
              (Array.isArray(codeClassName) &&
                codeClassName.some((cls) => cls.includes("mermaid"))) ||
              (typeof codeClassName === "string" &&
                codeClassName.includes("mermaid")) ||
              MERMAID_REGEX.test(content.trim());

            if (isMermaid) {
              return (
                <div>
                  <MemoizedMermaidComponent
                    diagram={content}
                    darkMode={darkMode}
                  />
                </div>
              );
            }

            const classNameStr = Array.isArray(codeClassName)
              ? codeClassName.join(" ")
              : codeClassName || "";

            const [, language = "plaintext"] =
              /language-(\w+)/.exec(classNameStr) ?? [];

            // CRITICAL: Never render code blocks for user messages
            // User messages should always render as plain text
            if (isUser) {
              return <div className="whitespace-pre-wrap">{content}</div>;
            }

            // CRITICAL: If this is in a <pre><code> block, it was wrapped in ``` in markdown
            // Trust that it's a code block, BUT override if it's clearly financial data
            if (looksLikeFinancialData(content)) {
              return <div className="whitespace-pre-wrap">{content}</div>;
            }

            // Otherwise, trust the markdown structure - it was wrapped in ```, so render as code
            return <ShikiCodeBlock code={content} language={language} />;
          }

          return <pre {...props}>{children}</pre>;
        },

        // Simplified inline code - no inline styles
        code: ({ node, inline, className, children, ...props }) => {
          // Only handle inline code (single backtick)
          if (inline) {
            const inlineContent = String(children).trim();

            // CRITICAL: For user messages, never apply code styling
            if (isUser) {
              return <span>{inlineContent}</span>;
            }

            // CRITICAL: If it looks like financial data, render as regular text
            if (looksLikeFinancialData(inlineContent)) {
              return <span>{inlineContent}</span>;
            }

            return <code className="inline-code">{children}</code>;
          }

          // If this is a standalone code element (not inline, not in pre),
          // it's likely a parsing edge case - render as regular text
          return <span>{children}</span>;
        },

        // Remove all other custom styling - let SCSS handle it
        blockquote: ({ node, children, ...props }) => (
          <blockquote {...props}>{children}</blockquote>
        ),

        p: ({ node, children, ...props }) => <p {...props}>{children}</p>,

        // Wrap tables in scrollable container
        table: ({ node, children, ...props }) => (
          <div className="table-container">
            <table {...props}>{children}</table>
          </div>
        ),

        // Citation text handler
        text: ({ children }) => {
          if (typeof children !== "string") return children;

          if (CITATION_REGEX.test(children)) {
            CITATION_REGEX.lastIndex = 0;

            const parts = [];
            let lastIndex = 0;
            let match;

            while ((match = CITATION_REGEX.exec(children)) !== null) {
              if (match.index > lastIndex) {
                parts.push(children.substring(lastIndex, match.index));
              }

              const citationIndex = match[1] || match[2] || match[3] || "?";
              const citationNumber = parseInt(citationIndex, 10);

              // Find the corresponding source URL from parsedSources
              const source =
                parsedSources && parsedSources.length > 0 && citationNumber > 0
                  ? parsedSources[citationNumber - 1] // Citations are 1-indexed
                  : null;

              parts.push(
                <Citation
                  key={`citation-${match.index}`}
                  index={citationIndex}
                  href={source?.url || null}
                  title={source?.title || null}
                  onClick={() =>
                    onCitationClick && onCitationClick(citationIndex)
                  }
                />
              );

              lastIndex = match.index + match[0].length;
            }

            if (lastIndex < children.length) {
              parts.push(children.substring(lastIndex));
            }

            return <>{parts}</>;
          }

          return children;
        },
      };
    }, [
      darkMode,
      onCitationClick,
      parsedSources,
      sources,
      isStreaming,
      isUser,
    ]);

    return (
      <TooltipProvider>
        <div
          className={cn("markdown-wrapper relative", textSizeClass, className)}
          {...props}
        >
          <ReactMarkdown
            allowedElements={allowedHTMLElements}
            className={cn(styles.MarkdownContent, "markdown-content")}
            components={components}
            remarkPlugins={remarkPlugins(limitedMarkdown)}
            rehypePlugins={rehypePlugins(true)}
          >
            {processedContent}
          </ReactMarkdown>
        </div>
      </TooltipProvider>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.children === nextProps.children &&
      prevProps.darkMode === nextProps.darkMode &&
      prevProps.isStreaming === nextProps.isStreaming
    );
  }
);

Markdown.propTypes = {
  children: PropTypes.string.isRequired,
  html: PropTypes.bool,
  limitedMarkdown: PropTypes.bool,
  darkMode: PropTypes.bool,
  onCitationClick: PropTypes.func,
  isStreaming: PropTypes.bool,
  className: PropTypes.string,
  sources: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  isUser: PropTypes.bool,
};

Markdown.displayName = "Markdown";

export default Markdown;
